import * as React from "react";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import startOfDay from "date-fns/startOfDay";
import { PickersDay, StaticDatePicker, PickersDayProps } from "@mui/x-date-pickers";
import { useEffect, useMemo, useState } from "react";
import { getDaysInMonth } from "date-fns";

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
  ...(selected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.dark,
    },
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  }),
}));

interface CalendarProps {
  currentYear: number;
  currentMonth: number;
  selectedValues: number[];
  minDate?: Date;
  maxDate?: Date;
  handleChange: (days: Date[]) => void;
}

export default function MultiDatePicker({
  currentYear,
  currentMonth,
  handleChange,
  selectedValues,
  minDate,
  maxDate,
}: CalendarProps) {
  const [firstRender, setFirstRender] = useState<boolean>(true);

  useEffect(() => {
    setFirstRender(false);
  }, []);

  const values = useMemo(() => {
    return selectedValues.map((value) => {
      var daysInMonth = getDaysInMonth(new Date(currentYear, currentMonth, 1));
      value = value <= daysInMonth ? value : 1; // to avoid exception in case a selected value loaded does not belong to the month
      return new Date(currentYear, currentMonth, value);
    });
  }, [selectedValues, currentYear, currentMonth]);

  const findDate = (dates: Date[], date: Date) => {
    const dateTime = date.getTime();
    return dates.find((item) => item.getTime() === dateTime);
  };

  const findIndexDate = (dates: Date[], date: Date) => {
    const dateTime = date.getTime();
    return dates.findIndex((item) => item.getTime() === dateTime);
  };

  const renderPickerDay = (date: Date, selectedDates: Date[], pickersDayProps: any) => {
    if (!values) {
      return <PickersDay {...pickersDayProps} />;
    }

    const selected = findDate(values, date);

    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        selected={selected}
      />
    );
  };

  return (
    <>
      <StaticDatePicker
        displayStaticWrapperAs="desktop"
        label="Picker"
        openTo="day"
        value={firstRender ? new Date(currentYear, currentMonth, 1) : values}
        // value={new Date(currentYear, currentMonth, 1)}
        disableHighlightToday
        onChange={(newValue) => {
          if (newValue != null) {
            const array = [...values];
            const date = startOfDay(newValue);
            const index = findIndexDate(array, date);
            if (index >= 0) {
              array.splice(index, 1);
            } else {
              array.push(date);
            }
            handleChange(array);
          }
        }}
        renderDay={renderPickerDay}
        renderInput={(params) => <TextField {...params} />}
        maxDate={maxDate ?? new Date(currentYear, currentMonth, getDaysInMonth(new Date(currentYear, currentMonth, 1)))}
        minDate={minDate ?? new Date(currentYear, currentMonth, 1)}
      />
    </>
  );
}
